import { z } from 'zod';
import { contentIdSchema } from '../rules/content-id.js';
import { customTypeSchema } from './form.js';
/**
 * Пользовательский контент по проводу (D53).
 *
 * Тело записи здесь не разобрано на поля: это `record` со свободными
 * ключами. Разбирает его схема вида — та самая, по которой
 * валидируется SRD (`srdContentSchema`), и делает это сервер при
 * сохранении. Второй схемы для того же самого не заводится: она
 * разошлась бы с первой, и запись, прошедшая проверку на клиенте,
 * получала бы отказ на сервере.
 *
 * Форма редактора приходит с сервера отдельным запросом
 * (`contentFormFields`), а не считается на клиенте: считать её значит
 * притащить в браузер схемы всех заклинаний, предметов и классов ради
 * экрана, который открывают раз в кампанию.
 */
const uuid = z.uuid();
/**
 * Ключ записи внутри владельца: вторая половина `u:<владелец>:<ключ>`.
 *
 * Латиница и подчёркивания — по той же причине, что у SRD (D40):
 * ключ попадает в условия эффектов и в провенанс, живёт годами
 * и не должен зависеть от раскладки.
 */
export const customKeySchema = z
    .string()
    .regex(/^[a-z0-9_]+$/, 'ключ: строчная латиница, цифры и подчёркивание')
    .min(2, 'ключ короче двух знаков ничего не назовёт')
    .max(60, 'ключ длиннее 60 знаков не помещается в ссылку');
/** Тело записи: то, что проверяет схема вида. */
export const contentBodySchema = z.record(z.string(), z.unknown());
export const customRecordSchema = z.strictObject({
    /** Внутренний идентификатор строки: по нему правят и удаляют. */
    id: uuid,
    /** Идентификатор в терминах правил: `u:<владелец>:<ключ>`. */
    contentId: contentIdSchema,
    type: customTypeSchema,
    name: z.string().min(1),
    body: contentBodySchema,
    /**
     * Сколько персонажей ссылается на запись (D53).
     *
     * Показывается рядом с правкой и удалением: молчаливое изменение
     * чисел на чужом листе — худший исход, и первое, что от него
     * защищает, — знать, что лист чужой вообще есть.
     */
    usedBy: z.number().int().nonnegative(),
    createdAt: z.iso.datetime(),
});
export const customListInputSchema = z.strictObject({
    /** `null` — все виды разом. */
    type: customTypeSchema.nullable(),
});
export const customListOutputSchema = z.array(customRecordSchema);
export const customRecordResultSchema = z.strictObject({
    record: customRecordSchema,
});
/**
 * Сохранение записи: и новой, и правки существующей.
 *
 * `confirmedUsedBy` — то самое подтверждение из D53. При правке
 * механики записи, на которую уже ссылаются персонажи, сервер
 * отказывает, пока мастер не подтвердит число затронутых листов.
 * Число, а не флаг: между «показали» и «нажал» кто-то мог создать
 * ещё одного персонажа, и подтверждение старого числа означает,
 * что мастер согласился не с тем.
 */
export const saveCustomInputSchema = z.strictObject({
    /** `null` — новая запись. */
    id: uuid.nullable(),
    type: customTypeSchema,
    key: customKeySchema,
    body: contentBodySchema,
    /** Откуда взято: заметка автора, а не ссылка на страницу (D53). */
    note: z.string().max(200).nullable(),
    confirmedUsedBy: z.number().int().nonnegative().nullable(),
});
export const removeCustomInputSchema = z.strictObject({
    id: uuid,
    /**
     * Удалить запись, на которую ссылаются. Без этого сервер откажет
     * и назовёт число листов: удаление ломает их расчёт (D53).
     */
    confirmedUsedBy: z.number().int().nonnegative().nullable(),
});
export const removeCustomResultSchema = z.strictObject({
    removed: z.boolean(),
});
/**
 * Отказ редактора: что именно не так и с какой записью.
 *
 * Отдельный вид отказа нужен потому, что «поле не прошло схему»
 * и «эту запись уже используют» — разные события, и второе не ошибка
 * мастера, а вопрос к нему.
 */
export const contentProblemSchema = z.strictObject({
    code: z.enum([
        /** Поле не прошло схему вида. */
        'invalid-field',
        /** Ключ занят другой записью того же владельца. */
        'key-taken',
        /** Запись используют персонажи; нужно подтверждение. */
        'in-use',
        /** Два `set` на одну величину (D18.3). */
        'conflicting-set',
    ]),
    /** Путь поля внутри записи; `null` — отказ не про поле. */
    path: z.string().nullable(),
    message: z.string().min(1),
    /** Сколько листов затронуто: только у `in-use`. */
    usedBy: z.number().int().nonnegative().nullable(),
});
export const contentRefusalSchema = z.strictObject({
    problems: z.array(contentProblemSchema).min(1),
});
/* ── Форма редактора ─────────────────────────────────────────────── */
const formFieldKindSchema = z.enum([
    'text',
    'longText',
    'number',
    'boolean',
    'enum',
    'contentRef',
    'effects',
    'resources',
    'list',
    'group',
    'variant',
    'unsupported',
]);
/**
 * Описание поля формы. Схема рекурсивна: группа содержит поля,
 * список — поля элемента, объединение — ветви со своими полями.
 * `z.lazy` здесь не украшение, а единственный способ выразить это
 * схемой, а не «доверием к серверу».
 */
export const formFieldSchema = z.lazy(() => z.strictObject({
    path: z.string().min(1),
    key: z.string().min(1),
    kind: formFieldKindSchema,
    required: z.boolean(),
    nullable: z.boolean(),
    options: z.array(z.string()).optional(),
    integer: z.boolean().optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    fields: z.array(formFieldSchema).optional(),
    variants: z.array(formVariantSchema).optional(),
    fallback: z.unknown().optional(),
}));
export const formVariantSchema = z.lazy(() => z.strictObject({
    value: z.string().min(1),
    fields: z.array(formFieldSchema),
}));
export const contentFormInputSchema = z.strictObject({ type: customTypeSchema });
export const contentFormOutputSchema = z.strictObject({
    fields: z.array(formFieldSchema),
});
/* ── Величины и ссылки для редактора эффектов (D53, шаг 4) ───────── */
/** Что можно менять эффектом: величина листа или набор признаков. */
export const effectTargetSchema = z.strictObject({
    path: z.string().min(1),
    /** Число или набор признаков: от этого зависят доступные операции. */
    kind: z.enum(['number', 'traits']),
});
/** Значение, на которое можно сослаться в `ref`. */
export const effectRefOptionSchema = z.strictObject({ path: z.string().min(1) });
export const effectVocabularyInputSchema = z.strictObject({
    /** Стол, чьи записи видны: словарь считается по видимому каталогу. */
    seatId: z.uuid().nullable(),
});
export const effectVocabularySchema = z.strictObject({
    targets: z.array(effectTargetSchema),
    refs: z.array(effectRefOptionSchema),
    /** Пути контекста, по которым ставятся условия. */
    conditionPaths: z.array(z.string().min(1)),
    /** Ключи признаков, известные из контента: для `grant`, `has`. */
    traits: z.array(z.string().min(1)),
});
/* ── Список для копирования (шаг 2) ──────────────────────────────── */
/**
 * Запись, которую можно взять за основу: имя и признак «своя».
 *
 * Тексты не приезжают: на шаге выбора нужны имя и вид, а описания
 * четырёхсот сорока шести предметов — мегабайт на каждое открытие.
 */
export const contentPickSchema = z.strictObject({
    id: contentIdSchema,
    name: z.string().min(1),
    custom: z.boolean(),
});
export const contentPickInputSchema = z.strictObject({
    type: customTypeSchema,
    /** Место, чей стол определяет видимость записей мастера (D30). */
    seatId: z.uuid().nullable(),
});
export const contentPickOutputSchema = z.array(contentPickSchema);
export const contentCopyInputSchema = z.strictObject({ id: contentIdSchema });
/** Тело записи-образца: им заполняется форма на шаге 3. */
export const contentCopyOutputSchema = z.strictObject({
    type: customTypeSchema,
    body: contentBodySchema,
});
//# sourceMappingURL=custom.js.map